import { useState, useMemo, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import {
	LineChart, Line, XAxis, YAxis, CartesianGrid,
	ReferenceLine,
} from 'recharts';
import {
	Search, Loader2, ChevronDown, TrendingDown,
	Target, Activity, Trophy, XCircle,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Input } from './ui/Input';
import { Checkbox } from './ui/Checkbox';
import { Popover, PopoverContent, PopoverTrigger } from './ui/Popover';
import {
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/Table';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from './ui/Chart';

const formatDollars = (value) =>
	new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);


/* ─── KPI Card ───────────────────────────────────────────── */
function KpiCard({ title, value, subtitle, icon: Icon, variant = 'default' }) {
	return (
		<Card className="gap-4">
			<CardHeader className="pb-0">
				<CardTitle className="flex items-center text-sm text-muted-foreground">
					{Icon && <Icon className="mr-2 size-4" />}
					{title}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<div className={cn(
					'text-2xl font-bold tabular-nums',
					variant === 'profit' && 'text-green-600',
					variant === 'loss' && 'text-red-600',
				)}>
					{value}
				</div>
				{subtitle && <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>}
			</CardContent>
		</Card>
	);
}

/* ─── Range Input Group ──────────────────────────────────── */
function RangeInputGroup({ label, value, onChange }) {
	return (
		<div className="space-y-1">
			<label className="text-xs font-medium text-muted-foreground">{label}</label>
			<div className="flex items-center gap-1">
				<Input
					type="number"
					className="h-8 w-[80px] text-xs"
					placeholder="Min"
					value={value.min}
					onChange={(e) => onChange({ ...value, min: Number(e.target.value) })}
					min="0"
				/>
				<span className="text-xs text-muted-foreground">to</span>
				<Input
					type="number"
					className="h-8 w-[80px] text-xs"
					placeholder="Max"
					value={value.max}
					onChange={(e) => onChange({ ...value, max: Number(e.target.value) })}
					min="0"
				/>
				<span className="text-xs text-muted-foreground">step</span>
				<Input
					type="number"
					className="h-8 w-[70px] text-xs"
					placeholder="Step"
					value={value.step}
					onChange={(e) => onChange({ ...value, step: Number(e.target.value) })}
					min="1"
				/>
			</div>
		</div>
	);
}

/* ─── Main Component ─────────────────────────────────────── */
export const PropFirmSimulator = () => {
	const { getAccessTokenSilently, user } = useAuth0();

	// Form state
	const today = new Date();
	const ninetyDaysAgo = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
	const formatDate = (d) => d.toISOString().split('T')[0];

	const [startDate, setStartDate] = useState(formatDate(ninetyDaysAgo));
	const [endDate, setEndDate] = useState(formatDate(today));
	const [selectedCombos, setSelectedCombos] = useState([]); // "algo|ticker" strings
	const [comboOptions, setComboOptions] = useState([]); // {algo, ticker} from API

	// Firm rules
	const [drawdownLimit, setDrawdownLimit] = useState(2500);
	const [profitTarget, setProfitTarget] = useState(3000);
	const [trailingDrawdown, setTrailingDrawdown] = useState(true);
	const [drawdownMode, setDrawdownMode] = useState('settled'); // 'settled' or 'intra-trade'
	const [stopWhenGreen, setStopWhenGreen] = useState(false);
	const [singleDirection, setSingleDirection] = useState(false);

	// Parameter ranges
	const [riskSizeRange, setRiskSizeRange] = useState({ min: 100, max: 500, step: 50 });
	const [dailyProfitRange, setDailyProfitRange] = useState({ min: 100, max: 1000, step: 50 });
	const [dailyLossRange, setDailyLossRange] = useState({ min: 100, max: 1000, step: 50 });
	const [maxTradesRange, setMaxTradesRange] = useState({ min: 0, max: 0, step: 1 }); // 0 = no limit

	// Data source
	const [dataSource, setDataSource] = useState('live'); // 'live' or 'backtest'

	// Analyze All
	const [analyzeAllMode, setAnalyzeAllMode] = useState(false);

	// Results
	const [results, setResults] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [selectedResultIdx, setSelectedResultIdx] = useState(null);
	const [riskFilter, setRiskFilter] = useState('all');

	const toggleCombo = (comboKey, max) => {
		setSelectedCombos((prev) => {
			if (prev.includes(comboKey)) return prev.filter((v) => v !== comboKey);
			if (max && prev.length >= max) return prev;
			return [...prev, comboKey];
		});
	};

	// Compute total combinations for preview
	const totalCombinations = useMemo(() => {
		const count = (r) => r.step > 0 ? Math.floor((r.max - r.min) / r.step) + 1 : 1;
		const maxTradesCount = maxTradesRange.max > 0 ? count(maxTradesRange) : 1;
		return count(riskSizeRange) * (stopWhenGreen ? 1 : count(dailyProfitRange)) * count(dailyLossRange) * maxTradesCount;
	}, [riskSizeRange, dailyProfitRange, dailyLossRange, stopWhenGreen, maxTradesRange]);

	// Parse "algo|ticker" strings into {algo, ticker} objects for the API
	const parseCombos = (comboKeys) =>
		comboKeys.map((k) => { const [algo, ticker] = k.split('|'); return { algo, ticker }; });

	// Pre-fetch available combo options on mount
	useEffect(() => {
		const fetchCombos = async () => {
			try {
				const token = await getAccessTokenSilently();
				const userId = user.sub.split('|')[1];
				const res = await axios.get(
					`${process.env.REACT_APP_SERVERLESS_API_URL}/api/v2/metrics/combos`,
					{ headers: { Authorization: `Bearer ${token}` }, params: { uid: userId } }
				);
				const combos = res.data?.combos || [];
				if (combos.length > 0) setComboOptions(combos);
			} catch (_) { /* combos will load with simulation results */ }
		};
		fetchCombos();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const runSimulation = async () => {
		setLoading(true);
		setError(null);
		setResults(null);
		setSelectedResultIdx(null);
		setRiskFilter('all');
		try {
			const token = await getAccessTokenSilently();
			const userId = user.sub.split('|')[1];
			const response = await axios.post(
				`${process.env.REACT_APP_SERVERLESS_API_URL}/api/v2/prop-firm/simulate`,
				{
					startDate,
					endDate,
					combos: selectedCombos.length > 0 ? parseCombos(selectedCombos) : undefined,
					singleDirection,
					dataSource: dataSource !== 'live' ? dataSource : undefined,
					firmRules: {
						drawdownLimit: Number(drawdownLimit),
						profitTarget: Number(profitTarget),
						trailingDrawdown,
						drawdownMode,
						stopWhenGreen,
					},
					paramRanges: {
						riskSize: riskSizeRange,
						dailyProfitLimit: dailyProfitRange,
						dailyLossLimit: dailyLossRange,
						...(maxTradesRange.max > 0 && { maxTradesPerDay: maxTradesRange }),
					},
					topN: 100,
				},
				{
					headers: { Authorization: `Bearer ${token}` },
					params: { uid: userId },
				}
			);
			setResults(response.data);
			if (response.data?.topResults?.length > 0) {
				setSelectedResultIdx(0);
			}
		} catch (err) {
			setError(err.response?.data?.error || err.message);
		} finally {
			setLoading(false);
		}
	};

	const runAnalyzeAll = async () => {
		if (selectedCombos.length === 0 || selectedCombos.length > 15) {
			setError('Select 1-5 algo+ticker combos for Analyze All');
			return;
		}
		setLoading(true);
		setError(null);
		setResults(null);
		setSelectedResultIdx(null);
		setRiskFilter('all');
		try {
			const token = await getAccessTokenSilently();
			const userId = user.sub.split('|')[1];
			const response = await axios.post(
				`${process.env.REACT_APP_SERVERLESS_API_URL}/api/v2/prop-firm/analyze-all`,
				{
					startDate,
					endDate,
					combos: parseCombos(selectedCombos),
					singleDirection,
					dataSource: dataSource !== 'live' ? dataSource : undefined,
					firmRules: {
						drawdownLimit: Number(drawdownLimit),
						profitTarget: Number(profitTarget),
						trailingDrawdown,
					},
					paramRanges: {
						riskSize: riskSizeRange,
						dailyProfitLimit: dailyProfitRange,
						dailyLossLimit: dailyLossRange,
						...(maxTradesRange.max > 0 && { maxTradesPerDay: maxTradesRange }),
					},
					topN: 10,
				},
				{
					headers: { Authorization: `Bearer ${token}` },
					params: { uid: userId },
				}
			);
			setResults({ ...response.data, isAnalyzeAll: true });
			if (response.data?.topResults?.length > 0) {
				setSelectedResultIdx(0);
			}
		} catch (err) {
			setError(err.response?.data?.error || err.message);
		} finally {
			setLoading(false);
		}
	};

	// Get unique risk sizes and filtered results
	const riskSizes = useMemo(() => {
		if (!results?.topResults) return [];
		const sizes = [...new Set(results.topResults.map(r => r.params.riskSize))];
		return sizes.sort((a, b) => a - b);
	}, [results]);

	const filteredResults = useMemo(() => {
		if (!results?.topResults) return [];
		if (riskFilter === 'all') {
			// Show best result for each risk size
			const bestByRisk = new Map();
			for (const r of results.topResults) {
				const key = r.params.riskSize;
				if (!bestByRisk.has(key) || r.passRate > bestByRisk.get(key).passRate) {
					bestByRisk.set(key, r);
				}
			}
			return [...bestByRisk.values()].sort((a, b) => a.params.riskSize - b.params.riskSize);
		}
		return results.topResults.filter(r => r.params.riskSize === Number(riskFilter)).slice(0, 25);
	}, [results, riskFilter]);

	const selectedResult = filteredResults?.[selectedResultIdx];

	// Build equity curve from the best attempt of the selected result
	const equityCurve = useMemo(() => {
		if (!selectedResult?.bestResult?.dailyLog) return [];
		return selectedResult.bestResult.dailyLog.map((d) => ({
			date: d.date,
			cumPnl: d.cumPnl,
		}));
	}, [selectedResult]);

	return (
		<div className="space-y-4">
			{/* ── Firm Rules ──────────────────────────────── */}
			<Card>
				<CardHeader>
					<CardTitle className="text-sm">Prop Firm Rules</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex flex-wrap items-end gap-4">
						<div className="space-y-1">
							<label className="text-xs font-medium text-muted-foreground">Drawdown Limit</label>
							<Input
								type="number"
								className="h-8 w-[120px] text-xs"
								value={drawdownLimit}
								onChange={(e) => setDrawdownLimit(Number(e.target.value))}
								min="0"
							/>
						</div>
						<div className="space-y-1">
							<label className="text-xs font-medium text-muted-foreground">Profit Target</label>
							<Input
								type="number"
								className="h-8 w-[120px] text-xs"
								value={profitTarget}
								onChange={(e) => setProfitTarget(Number(e.target.value))}
								min="0"
							/>
						</div>
						<div className="space-y-1">
							<label className="text-xs font-medium text-muted-foreground">Drawdown Type</label>
							<div className="flex items-center gap-2 h-8">
								<label className="flex items-center gap-1.5 text-xs cursor-pointer">
									<Checkbox
										checked={trailingDrawdown}
										onCheckedChange={(checked) => setTrailingDrawdown(!!checked)}
									/>
									Trailing
								</label>
							</div>
						</div>
						{!analyzeAllMode && (
							<div className="space-y-1">
								<label className="text-xs font-medium text-muted-foreground">Drawdown Calculation</label>
								<div className="flex items-center gap-3 h-8">
									<label className="flex items-center gap-1.5 text-xs cursor-pointer">
										<input
											type="radio"
											name="drawdownMode"
											value="intra-trade"
											checked={drawdownMode === 'intra-trade'}
											onChange={() => setDrawdownMode('intra-trade')}
											className="size-3"
										/>
										Intra-trade
									</label>
									<label className="flex items-center gap-1.5 text-xs cursor-pointer">
										<input
											type="radio"
											name="drawdownMode"
											value="settled"
											checked={drawdownMode === 'settled'}
											onChange={() => setDrawdownMode('settled')}
											className="size-3"
										/>
										Settled only
									</label>
								</div>
							</div>
						)}
						{!analyzeAllMode && (
							<div className="space-y-1">
								<label className="text-xs font-medium text-muted-foreground">Profit Strategy</label>
								<div className="flex items-center gap-2 h-8">
									<label className="flex items-center gap-1.5 text-xs cursor-pointer">
										<Checkbox
											checked={stopWhenGreen}
											onCheckedChange={(checked) => setStopWhenGreen(!!checked)}
										/>
										Stop when green
									</label>
								</div>
							</div>
						)}
						{analyzeAllMode && (
							<div className="space-y-1">
								<p className="text-xs text-muted-foreground mt-2">
									Analyze All tests all 3 modes automatically: Settled, Intra-trade, and Stop When Green
								</p>
							</div>
						)}
						<div className="space-y-1">
							<label className="text-xs font-medium text-muted-foreground">Trade Filtering</label>
							<div className="flex items-center gap-2 h-8">
								<label className="flex items-center gap-1.5 text-xs cursor-pointer">
									<Checkbox
										checked={singleDirection}
										onCheckedChange={(checked) => setSingleDirection(!!checked)}
									/>
									Single Direction Trading
								</label>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* ── Filters & Parameter Ranges ──────────────── */}
			<Card>
				<CardHeader>
					<CardTitle className="text-sm">Filters & Parameter Sweep</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					{/* Filters row */}
					<div className="flex flex-wrap items-end gap-3">
						{/* Algo + Ticker combo filter */}
						<div className="space-y-1">
							<label className="text-xs font-medium text-muted-foreground">
								Algo + Ticker{analyzeAllMode && ` (max 15, ${selectedCombos.length} selected)`}
							</label>
							<Popover>
								<PopoverTrigger asChild>
									<Button variant="outline" size="sm" className="h-8 w-[200px] justify-between text-xs">
										<span className="truncate">
											{selectedCombos.length === 0
												? 'All combos'
												: selectedCombos.length === 1
													? selectedCombos[0].replace('|', ' — ')
													: `${selectedCombos.length} selected`}
										</span>
										<ChevronDown className="ml-1.5 size-3 shrink-0" />
									</Button>
								</PopoverTrigger>
								<PopoverContent className="w-64 p-2" align="start">
									<div className="max-h-72 space-y-0.5 overflow-y-auto">
										{comboOptions.length > 0 ? comboOptions.map((c) => {
											const key = `${c.algo}|${c.ticker}`;
											return (
												<label
													key={key}
													className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent"
												>
													<Checkbox
														checked={selectedCombos.includes(key)}
														onCheckedChange={() => toggleCombo(key, analyzeAllMode ? 15 : undefined)}
													/>
													{c.algo} — {c.ticker}
												</label>
											);
										}) : (
											<p className="px-2 py-1.5 text-xs text-muted-foreground">
												Click "Load Options" to fetch available combos
											</p>
										)}
									</div>
								</PopoverContent>
							</Popover>
						</div>

						{/* Date range */}
						<div className="space-y-1">
							<label className="text-xs font-medium text-muted-foreground">Start Date</label>
							<Input
								type="date"
								className="h-8 w-[140px] text-xs"
								value={startDate}
								onChange={(e) => setStartDate(e.target.value)}
							/>
						</div>
						<div className="space-y-1">
							<label className="text-xs font-medium text-muted-foreground">End Date</label>
							<Input
								type="date"
								className="h-8 w-[140px] text-xs"
								value={endDate}
								onChange={(e) => setEndDate(e.target.value)}
							/>
						</div>
					</div>

					{/* Parameter ranges */}
					<div className="flex flex-wrap gap-6">
						<RangeInputGroup
							label="Risk Size ($)"
							value={riskSizeRange}
							onChange={setRiskSizeRange}
						/>
						{!stopWhenGreen && (
							<RangeInputGroup
								label="Daily Profit Limit ($)"
								value={dailyProfitRange}
								onChange={setDailyProfitRange}
							/>
						)}
						<RangeInputGroup
							label="Daily Loss Limit ($)"
							value={dailyLossRange}
							onChange={setDailyLossRange}
						/>
						<RangeInputGroup
							label="Max Trades/Day (0=no limit)"
							value={maxTradesRange}
							onChange={setMaxTradesRange}
						/>
					</div>

					{/* Data source toggle */}
					<div className="flex items-center gap-3">
						<label className="text-xs font-medium text-muted-foreground">Data Source:</label>
						<label className="flex items-center gap-1.5 text-xs cursor-pointer">
							<input
								type="radio"
								name="dataSource"
								value="live"
								checked={dataSource === 'live'}
								onChange={() => setDataSource('live')}
								className="size-3"
							/>
							Live
						</label>
						<label className="flex items-center gap-1.5 text-xs cursor-pointer">
							<input
								type="radio"
								name="dataSource"
								value="backtest"
								checked={dataSource === 'backtest'}
								onChange={() => setDataSource('backtest')}
								className="size-3"
							/>
							Backtest
						</label>
					</div>

					{/* Run button */}
					<div className="flex items-center gap-3">
						<Button
							size="sm"
							className="h-8 text-xs"
							onClick={analyzeAllMode ? runAnalyzeAll : runSimulation}
							disabled={loading || (analyzeAllMode && (selectedCombos.length === 0 || selectedCombos.length > 15))}
						>
							{loading ? (
								<Loader2 className="mr-1 size-3 animate-spin" />
							) : (
								<Search className="mr-1 size-3" />
							)}
							{loading ? 'Analyzing...' : analyzeAllMode ? 'Analyze All' : 'Run Simulation'}
						</Button>
						<label className="flex items-center gap-1.5 text-xs cursor-pointer">
							<Checkbox
								checked={analyzeAllMode}
								onCheckedChange={(checked) => setAnalyzeAllMode(!!checked)}
							/>
							Analyze All Combos
						</label>
						{!analyzeAllMode && (
							<span className="text-xs text-muted-foreground">
								{totalCombinations.toLocaleString()} combinations
							</span>
						)}
						{analyzeAllMode && selectedCombos.length > 0 && (
							<span className="text-xs text-muted-foreground">
								{Math.pow(2, selectedCombos.length) - 1} combos × 3 modes
							</span>
						)}
					</div>
				</CardContent>
			</Card>

			{/* ── Error ──────────────────────────────────── */}
			{error && (
				<div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
					{error}
				</div>
			)}

			{/* ── Loading ────────────────────────────────── */}
			{loading && (
				<div className="flex items-center justify-center py-20">
					<Loader2 className="size-8 animate-spin text-muted-foreground" />
				</div>
			)}

			{/* ── Results ────────────────────────────────── */}
			{results && !loading && (
				<div className="space-y-4">
					{/* Summary KPIs */}
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
						<KpiCard
							title={results.isAnalyzeAll ? 'Algo Combos Tested' : 'Combinations Tested'}
							value={results.isAnalyzeAll ? `${results.totalAlgoCombos} × 3 modes` : results.totalCombinations.toLocaleString()}
							subtitle={`${results.tradeCount.toLocaleString()} trades across ${results.tradingDays} days`}
							icon={Activity}
						/>
						<KpiCard
							title="Best Pass Rate"
							value={results.topResults.length > 0 ? `${(results.topResults[0].passRate * 100).toFixed(1)}%` : '0%'}
							subtitle={results.topResults.length > 0 ? `${results.topResults[0].passes}/${results.topResults[0].passes + results.topResults[0].drawdownFails} decisive attempts` : ''}
							icon={Trophy}
							variant={results.topResults[0]?.passRate > 0 ? 'profit' : 'loss'}
						/>
						<KpiCard
							title="Drawdown Limit"
							value={formatDollars(drawdownLimit)}
							subtitle={trailingDrawdown ? 'Trailing' : 'Static'}
							icon={TrendingDown}
						/>
						<KpiCard
							title="Profit Target"
							value={formatDollars(profitTarget)}
							icon={Target}
						/>
					</div>

					{/* Results table */}
					{(results.isAnalyzeAll ? results.topResults.length > 0 : filteredResults.length > 0) && (
						<Card>
							<CardHeader>
								<div className="flex items-center justify-between">
									<CardTitle className="text-sm">
										{results.isAnalyzeAll ? 'Best Algo Combinations' : 'Top Parameter Combinations'}
									</CardTitle>
									{!results.isAnalyzeAll && (
										<div className="flex items-center gap-1">
											<Button
												variant={riskFilter === 'all' ? 'default' : 'outline'}
												size="sm"
												className="h-7 text-xs px-2"
												onClick={() => { setRiskFilter('all'); setSelectedResultIdx(null); }}
											>
												All
											</Button>
											{riskSizes.map(size => (
												<Button
													key={size}
													variant={riskFilter === String(size) ? 'default' : 'outline'}
													size="sm"
													className="h-7 text-xs px-2"
													onClick={() => { setRiskFilter(String(size)); setSelectedResultIdx(null); }}
												>
													${size}
												</Button>
											))}
										</div>
									)}
								</div>
							</CardHeader>
							<CardContent>
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead className="w-8">#</TableHead>
											{results.isAnalyzeAll && <TableHead>Algo Combo</TableHead>}
											{results.isAnalyzeAll && <TableHead>Mode</TableHead>}
											<TableHead className="text-right">Risk $</TableHead>
											{!results.isAnalyzeAll && !stopWhenGreen && <TableHead className="text-right">Daily Profit Limit</TableHead>}
											{results.isAnalyzeAll && <TableHead className="text-right">Profit Strategy</TableHead>}
											<TableHead className="text-right">Daily Loss Limit</TableHead>
											<TableHead className="text-right">Eff. Max Loss</TableHead>
											{maxTradesRange.max > 0 && <TableHead className="text-right">Max Trades</TableHead>}
											<TableHead className="text-right">Pass Rate</TableHead>
											<TableHead className="text-right">Passes</TableHead>
											<TableHead className="text-right">DD Fails</TableHead>
											<TableHead className="text-right">Avg Days</TableHead>
											<TableHead className="text-right">Worst Max DD</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{(results.isAnalyzeAll ? results.topResults : filteredResults).map((r, idx) => (
											<TableRow
												key={idx}
												className={cn(
													'cursor-pointer',
													selectedResultIdx === idx && 'bg-accent',
												)}
												onClick={() => setSelectedResultIdx(idx)}
											>
												<TableCell className="text-muted-foreground">{idx + 1}</TableCell>
												{results.isAnalyzeAll && <TableCell className="font-medium text-xs">{r.params.algoCombo}</TableCell>}
												{results.isAnalyzeAll && <TableCell className="text-xs">{r.params.mode}</TableCell>}
												<TableCell className="text-right font-medium">{formatDollars(r.params.riskSize)}</TableCell>
												{!results.isAnalyzeAll && !stopWhenGreen && <TableCell className="text-right">{typeof r.params.dailyProfitLimit === 'string' ? r.params.dailyProfitLimit : formatDollars(r.params.dailyProfitLimit)}</TableCell>}
												{results.isAnalyzeAll && <TableCell className="text-right text-xs">{typeof r.params.dailyProfitLimit === 'string' ? r.params.dailyProfitLimit : formatDollars(r.params.dailyProfitLimit)}</TableCell>}
												<TableCell className="text-right">{formatDollars(r.params.dailyLossLimit)}</TableCell>
												<TableCell className="text-right text-orange-600">{r.params.effectiveMaxLoss != null && r.params.effectiveMaxLoss !== r.params.dailyLossLimit ? formatDollars(r.params.effectiveMaxLoss) : '—'}</TableCell>
												{maxTradesRange.max > 0 && <TableCell className="text-right">{r.params.maxTradesPerDay}</TableCell>}
												<TableCell className="text-right">
													<Badge variant={r.passRate >= 0.5 ? 'profit' : r.passRate > 0 ? 'default' : 'loss'}>
														{(r.passRate * 100).toFixed(1)}%
													</Badge>
												</TableCell>
												<TableCell className="text-right text-green-600">{r.passes}/{r.passes + r.drawdownFails}</TableCell>
												<TableCell className="text-right text-red-600">{r.drawdownFails}</TableCell>
												<TableCell className="text-right">{r.avgDaysToPass ?? '—'}</TableCell>
												<TableCell className="text-right text-red-600">
													{formatDollars(r.worstMaxDrawdown)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</CardContent>
						</Card>
					)}

					{/* Equity curve for best attempt of selected result */}
					{selectedResult && equityCurve.length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle className="text-sm">
									Best Attempt — Risk ${selectedResult.params.riskSize},
									Daily Profit ${selectedResult.params.dailyProfitLimit},
									Daily Loss ${selectedResult.params.dailyLossLimit}
									{selectedResult.params.effectiveMaxLoss != null && selectedResult.params.effectiveMaxLoss !== selectedResult.params.dailyLossLimit && (
										<span className="text-orange-600"> (eff. max ${selectedResult.params.effectiveMaxLoss})</span>
									)}
									<Badge variant="profit" className="ml-2 gap-1">
										<Trophy className="size-3" /> {(selectedResult.passRate * 100).toFixed(1)}% pass rate
									</Badge>
									{selectedResult.bestResult && (
										<span className="ml-2 text-xs text-muted-foreground">
											Starting {selectedResult.bestResult.startDate} — passed in {selectedResult.bestResult.tradeDays} days
										</span>
									)}
								</CardTitle>
							</CardHeader>
							<CardContent>
								<ChartContainer
									config={{ cumPnl: { label: 'Cumulative P&L', color: 'hsl(var(--chart-1))' } }}
									className="h-[300px]"
								>
									<LineChart data={equityCurve}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis dataKey="date" tick={{ fontSize: 11 }} />
										<YAxis tick={{ fontSize: 11 }} />
										<ChartTooltip content={<ChartTooltipContent formatter={(v) => formatDollars(v)} />} />
										<ReferenceLine y={0} stroke="hsl(var(--border))" />
										<ReferenceLine
											y={profitTarget}
											stroke="hsl(var(--chart-3))"
											strokeDasharray="5 5"
											label={{ value: `Target: ${formatDollars(profitTarget)}`, position: 'right', fontSize: 11 }}
										/>
										<ReferenceLine
											y={-drawdownLimit}
											stroke="hsl(var(--destructive))"
											strokeDasharray="5 5"
											label={{ value: `DD Limit: -${formatDollars(drawdownLimit)}`, position: 'right', fontSize: 11 }}
										/>
										<Line type="monotone" dataKey="cumPnl" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} />
									</LineChart>
								</ChartContainer>
							</CardContent>
						</Card>
					)}

					{/* No passing results */}
					{results.topResults.length > 0 && results.topResults[0].passRate === 0 && (
						<div className="py-8 text-center">
							<XCircle className="mx-auto mb-4 size-12 text-red-400 opacity-60" />
							<h3 className="mb-2 text-lg font-medium text-foreground">No Passing Combinations</h3>
							<p className="text-sm text-muted-foreground">
								None of the {results.totalCombinations} parameter combinations passed the challenge from any starting point.
								Try widening your parameter ranges or adjusting the prop firm rules.
							</p>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
